/**
 * @module Permission/Service
 */

import { Injectable } from '@angular/core';
import { User } from '@feature/users/shared';
import { CompanyMemberRef, CompanyMemberRole } from '@feature/companies/shared';
import { PermissionContextService } from '@app/services/permissions';

/**
 * Service that manages permission validation within the application.
 *
 * The permission validation Singleton class defines the static methods
 * that let client access the unique singleton instance, rather than creating a new instance.
 *
 * IMPORTANT: The sequence of CompanyMemberRole roles has to be kept in a specific order.
 * Less valuable roles has to be added to the end of the list. When they are matched,
 * they are actually indexed to each other.
 *
 * @example
 * PermissionValidationService.canCreatePayment(this.permissionContextService).then((hasPermission: boolean) => hasPermission);
 *
 * @see [The static method that controls the access to the singleton instance.](https://stackoverflow.com/a/68891150/15391077)
 * @see [Singleton in TypeScript](https://refactoring.guru/design-patterns/singleton/typescript/example)
 */
@Injectable({ providedIn: 'root' })
export class PermissionValidationService {
	/**
	 * Checks if the current context belongs to a super administrator.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static isSuperAdmin(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(context.isSuperAdmin());
	}

	/**
	 * Checks if the current context doesn't belong to a super administrator.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static isNotSuperAdmin(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(! context.isSuperAdmin());
	}

	static isCustomer(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(context.isCustomer());
	}

	static isTalent(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(context.isTalent());
	}

	static isAgent(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(context.isAgent());
	}

	static isNotAgent(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(! context.isAgent());
	}

	/**
	 * Checks if the current context belongs to a new user.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static isNew(context: PermissionContextService): Promise<boolean> {
		return Promise.resolve(context.isNew());
	}

	/**
	 * Checks if the current context has the profile type selected.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static hasType(context: PermissionContextService): Promise<boolean> {
		if (context.currentContext instanceof CompanyMemberRef) return Promise.resolve((context.currentContext as CompanyMemberRef)?.companyType != null);
		if (context.currentContext instanceof User) return Promise.resolve((context.currentContext as User)?.roles != null && (context.currentContext as User)?.roles[0]?.alias != null);
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context has no profile type selected.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static hasNoType(context: PermissionContextService): Promise<boolean> {
		if (context.currentContext instanceof CompanyMemberRef) return Promise.resolve((context.currentContext as CompanyMemberRef)?.companyType == null);
		if (context.currentContext instanceof User) return Promise.resolve((context.currentContext as User)?.roles == null || ((context.currentContext as User)?.roles != null && (context.currentContext as User)?.roles[0]?.alias == null));
		return Promise.resolve(true);
	}

	/**
	 * Checks if the current context is a client with agents.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static hasAgents(context: PermissionContextService): Promise<boolean> {
		if (context.hasTypes()) {
			if (context.isTalent() && (context.currentContext as User)?.agents != null) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context is agent, or a client with agents.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static isAgentOrHasAgents(context: PermissionContextService): Promise<boolean> {
		if (context.hasTypes()) {
			if ((context.isAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.owner, true))
			|| (context.isTalent() && (context.currentContext as User)?.agents != null)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can access profile preferences.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canEditPreferences(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.isNew()) return this.isNew(context);
		if (context.hasTypes()) {
			if (context.isTalent()
			|| (context.isCustomer() && ! context.isCompanyContext())
			|| (context.isCustomerOrAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.owner, true))) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can view payment.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canViewPayment(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(true);
		if (context.isNew()) return this.isNew(context);
		if (context.hasTypes()) {
			if (context.isTalent()
			|| (context.isCustomerOrAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.viewer))) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can create a union payment.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canCreateUnionPayment(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.isNew()) return this.isNew(context);
		if (context.hasTypes()) { // Payer can be or a company, or an individual payer.
			if (context.isCustomer() && (
				(! context.isMember() && (
					// ETT-551 Allow both customer and talent to create payments.
					((context.currentContext instanceof User) && (context.currentContext as User)?.isCustomer))
				) || (context.isMember() && context.roleIsEqualOrBelow(CompanyMemberRole.editor))
			)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can create a non-performance payment.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canCreateVendorPayment(context: PermissionContextService): Promise<boolean> {
		const canPayOrRequestAsVendor: boolean = (
			(context.currentContext as User)?.canPayOrRequestAsVendor
		|| (context.currentContext as CompanyMemberRef)?.companyCanPayOrRequestAsVendor);
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.isNew()) return this.isNew(context);
		if (context.hasTypes()) { // Payer can be or a company, or an individual payer.
			if ((context.isCustomer() || context.isTalent()) && (
				(! context.isMember() && (
					// ETT-551 Allow both customer and talent to create payments.
					((context.currentContext instanceof User) && (context.currentContext as User)?.isCustomer)
					|| ((context.currentContext instanceof User) && (context.currentContext as User)?.isTalent && canPayOrRequestAsVendor))
				) || (context.isMember() && context.roleIsEqualOrBelow(CompanyMemberRole.editor) && canPayOrRequestAsVendor)
			)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can import payment.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canImportPayment(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.isNew()) return this.isNew(context);
		if (context.hasTypes()) { // Payer can be or a company, or an individual payer.
			// @note ETT-516 Currently, the ability to import payments is limited to members of the enterprise company.
			if (context.isEnterpriseCustomer() && (context.isMember() && context.roleIsEqualOrBelow(CompanyMemberRole.editor))) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can create any kind of payment.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static async canCreatePayment(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin() || context.isNew()) return Promise.resolve(false);

		const canCreateUnionPayment = await PermissionValidationService.canCreateUnionPayment(context);
		const canCreateVendorPayment = await PermissionValidationService.canCreateVendorPayment(context);
		return Promise.resolve(canCreateUnionPayment || canCreateVendorPayment);
	}

	/**
	 * Checks if the current context can view clients.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canViewClients(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.hasTypes()) {
			if (context.isAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.viewer)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can add, edit or delete clients.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canAddEditDeleteClient(context: PermissionContextService): Promise<boolean> {
		if (context.hasTypes()) {
			if (context.isAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.editor)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can view contributors.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canViewContributors(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		if (context.hasTypes()) {
			if ((context.isAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.viewer))
			|| context.isClient()) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can upload files to a client's profile.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canUploadDocsToClient(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return this.isSuperAdmin(context);
		if (context.hasTypes()) {
			if (context.isAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.editor)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Checks if the current context can view members.
	 *
	 * @param {PermissionContextService} context
	 * @param {number} memberId
	 * @returns {Promise<boolean>}
	 */
	static canViewMembers(context: PermissionContextService, memberId?: number): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(false);
		// ETT-216 Restrict current context user from performing actions on himself as member.
		if (context.isSameUser(memberId)) { return Promise.resolve(false); }
		if (context.hasTypes()) {
			if ((context.isCompanyCustomer() || context.isAgent() || context.isMember()) && context.roleIsEqualOrBelow(CompanyMemberRole.viewer)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Determines if the current context user can add/edit/delete a member.
	 *
	 * @param {PermissionContextService} context
	 * @param {number} memberId
	 * @returns {Promise<boolean>}
	 */
	static canAddEditDeleteMember(context: PermissionContextService, memberId?: number): Promise<boolean> {
		if (context.hasTypes()) {
			if ((context.isCompanyCustomer() || context.isAgent() || context.isMember()) && context.roleIsEqualOrBelow(CompanyMemberRole.admin)) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Determines if the current context can invite a performer.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canInvitePerformer(context: PermissionContextService): Promise<boolean> {
		if (context.hasTypes()) { // Payer can be or a company, or an individual payer.
			if (context.isCustomer() && (((context.currentContext as User)?.isCustomer) || context.roleIsEqualOrBelow(CompanyMemberRole.admin))) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Determines if the current context can edit a performer's profile.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canEditProfile(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return this.isSuperAdmin(context);
		if (context.hasTypes()) {
			if (context.isTalent()
			|| context.isIndividualCustomer()
			|| (context.isCustomerOrAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.owner, true))) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Check if the current context can edit the profile taxation info.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canEditProfileTaxationInfo(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return this.isSuperAdmin(context);
		if (context.hasTypes()) {
			if ((context.isTalent()
			|| context.isIndividualCustomer()
			|| (context.isCustomerOrAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.owner, true)))
			&& context.hasAccessToProfileStep('taxation')) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Check if the current context can edit the profile banking info.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static canEditProfileFinancialInfo(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return this.isSuperAdmin(context);
		if (context.hasTypes()) {
			if ((context.isTalent()
			|| context.isIndividualCustomer()
			|| (context.isCustomerOrAgent() && context.roleIsEqualOrBelow(CompanyMemberRole.owner, true)))
			&& context.hasAccessToProfileStep('banking')) {
				return Promise.resolve(true);
			}
		}
		return Promise.resolve(false);
	}

	/**
	 * Check if the current location path is Company Team's page.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static isNotTeamPage(context: PermissionContextService): Promise<boolean> {
		const routerUrl = context.getCurrentUrl();
		return Promise.resolve(routerUrl !== '/companies/team');
	}

	/**
	 * Check if the current context can access the Admin group.
	 *
	 * @param {PermissionContextService} context
	 * @returns {Promise<boolean>}
	 */
	static async canAccessAdminGroup(context: PermissionContextService): Promise<boolean> {
		if (context.isSuperAdmin()) return Promise.resolve(true);

		const canViewMembers = await PermissionValidationService.canViewMembers(context);
		const canViewClients = await PermissionValidationService.canViewClients(context);
		const hasAgents = await PermissionValidationService.hasAgents(context);

		let result = false;
		if (canViewMembers) result = true;
		else if (canViewClients) result = true;
		else if (hasAgents) result = true;

		return Promise.resolve(result);
	}
}
